import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useMixpanel from '@/hooks/useMixpanel';
import useLog from '@/hooks/useLog';
import { AccountContext } from '@/context/account.context';
import { applyFilters } from '@/utils/filters';
import { isPresent, resourceHelpers } from '@/utils/helpers';
import t from '@/utils/Language/ResourceCard-translations';
import classes from './ResourceAccessInstruction.module.scss';

// Whether ResourceAccessInstruction will render anything for this instruction.
// Parents that wrap each instruction in their own container should filter with
// this first, so a non-rendering instruction doesn't leave an empty wrapper.
export const rendersInstruction = (instruction) => {
  if (instruction.access_type === 'email') {
    // this case
    return instruction.emails?.length > 0;
  }
  return ['location', 'phone', 'link', 'other'].includes(instruction.access_type);
};

// Whether this instruction gives the member something to act on -- a link, a tel:/mailto:
// target, or an address that resolves to a real destination. A location instruction can
// render with no locations at all, or with locations that never got a street address, in
// which case it is still worth showing (the notes may explain where to go) but must not
// present itself as clickable. Parents use this to decide hover/caret/click affordances.
export const instructionIsInteractive = (instruction) => {
  switch (instruction.access_type) {
    case 'email':
      return instruction.emails?.length > 0;
    case 'location':
      return instruction.locations?.some(resourceHelpers.hasMappableAddress) ?? false;
    case 'phone':
    case 'link':
      return isPresent(instruction.access_value);
    default:
      return false;
  }
};

const ResourceAccessInstruction = ({ instruction, addressBlockLayout = 'block', showNotes = true, showIconCircle = false, ...props }) => {
  const { track } = useMixpanel();
  const { log } = useLog();
  const account = useContext(AccountContext);

  const handleClick = (accessType) => {
    if (!props?.resource) return;

    const eventHash = resourceHelpers.mixpanelEventHash(props.resource);
    // usually the convention would be camel cased property names but leaving access_type snake case so as not to change existing tracking for FamilyResourceFinder project
    track('Identified need', { ...eventHash, sourceEvent: 'access instruction', access_type: accessType });

    if (account.isLoggedIn && !account.isPro) {
      log({
        event: 'identified need',
        via: 'access instruction',
        message: accessType,
        object_type: props.resource.resource_type,
        object_id: props.resource.id
      });
    }
  };

  const renderIcon = (icon, iconType = 'fas') => {
    if (showIconCircle) {
      return (
        <span className={classes.iconCircle}>
          <FontAwesomeIcon icon={[iconType, icon]} className={`${classes.icon} ${classes.iconBlue}`} />
        </span>
      );
    }
    return <FontAwesomeIcon icon={[iconType, icon]} className={classes.icon} />;
  };

  const containerClass = showIconCircle
    ? `${classes.flexContainer} ${classes.instructionContainer}`
    : `${classes.iconContainer} ${classes.instructionContainer}`;

  return (
    <>
      {instruction.access_type == 'location' && (
        <div className={containerClass}>
          {renderIcon('map-marker-alt')}
          <div className={showIconCircle ? classes.flexContent : undefined}>
            <p className={`${classes.instructionText} ${classes.bold}`}>{t('Go in person')}</p>
            {showNotes && <p className={`${classes.instructionText} ${classes.notes}`}>{instruction.instructions}</p>}
            {/* A div, not a p: generateAddressBlock returns an <address>, which is flow content
                and cannot legally nest inside a <p>. The browser reparsing the SSR HTML would
                auto-close the p and hoist the address out, breaking hydration and this onClick. */}
            {instruction.locations?.map((location, index) => {
              const mappable = resourceHelpers.hasMappableAddress(location);
              return (
                <div className={classes.location} key={index} onClick={mappable ? () => handleClick('location') : undefined}>
                  {resourceHelpers.generateAddressBlock({ location, includeName: true, makeDirectionLink: mappable, layout: addressBlockLayout })}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {instruction.access_type == 'phone' && (
        <div className={containerClass}>
          {renderIcon('phone')}
          <div className={showIconCircle ? classes.flexContent : undefined}>
            <a href={'tel:' + instruction.access_value} onClick={() => handleClick('phone')}>
              {t('Call')} {applyFilters(['phone'], instruction.access_value)}
            </a>
            {showNotes && <p className={`${classes.instructionText} ${classes.notes}`}>{instruction.instructions}</p>}
          </div>
        </div>
      )}
      {instruction.access_type == 'email' && instruction.emails && instruction.emails.length > 0 && (
        <div className={containerClass}>
          {renderIcon('envelope')}
          <div className={showIconCircle ? classes.flexContent : undefined}>
            <a href={'mailTo:' + instruction.emails[0].email} onClick={() => handleClick('email')}>
              {t('Email')} {instruction.emails[0].email}
              {resourceHelpers.emailMeta(instruction.emails[0]) && <span>({resourceHelpers.emailMeta(instruction.emails[0])})</span>}
            </a>
            {showNotes && <p className={`${classes.instructionText} ${classes.notes}`}>{instruction.instructions}</p>}
          </div>
        </div>
      )}
      {instruction.access_type == 'link' && (
        <div className={containerClass}>
          {renderIcon('link')}
          <div className={showIconCircle ? classes.flexContent : undefined}>
            <a href={instruction.access_value} target="_blank" rel="noreferrer" onClick={() => handleClick('link')}>
              {t('Click to visit link')} - {applyFilters(['truncateLink'], instruction.access_value)}
            </a>
            {showNotes && <p className={`${classes.instructionText} ${classes.notes}`}>{instruction.instructions}</p>}
          </div>
        </div>
      )}
      {instruction.access_type == 'other' && (
        <div
          className={
            showIconCircle
              ? `${classes.flexContainer} ${classes.instructionContainer}`
              : `${classes.instructionContainer} ${classes.otherAccessInstruction}`
          }
        >
          {showIconCircle && renderIcon('circle-info')}
          <div className={showIconCircle ? classes.flexContent : undefined}>
            <p className={classes.instructionText}>{applyFilters(['linkify', 'paragrapherize'], instruction.access_value)}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ResourceAccessInstruction;
